import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNowStrict } from 'date-fns';
import { LoadingPage } from '../../components/common/Spinner';
import { Button } from '../../components/common/Button';
import { ConfirmModal } from '../../components/common/Modal';
import { useRobotTasks, useCancelRobotTask, useRetryRobotTask } from '../../hooks/useRobotTasks';
import { formatDisplayDateTime } from '../../utils/format';
import type { PendingQuestionSummary, RobotTaskRow } from '../../services/api/robotTasks';
import { ApiClientError } from '../../services/api/client';

/**
 * Phase 115 (Plan 115-06 / PCOPS-02, D-115-16, D-115-17, D-115-18) — the
 * standalone operator dashboard: one page, every robot task across BOTH
 * executors, filterable, with an audited cancel and retry.
 *
 * Structural clone of `BotAdminPage.tsx`'s LoadingPage guard, "Try again"
 * error block, empty state, and table shell (no tabs — D-115-16 explicitly
 * wants a standalone page, not a third `BotAdminPage` tab). Row actions use
 * the SAME `pendingRowAction` + `ConfirmModal` pairing, never the browser's
 * own confirm/alert.
 */

type RowAction = { kind: 'cancel' | 'retry'; row: RobotTaskRow };

const TERMINAL_PAPERCLIP_RUN_STATUSES = new Set(['finished', 'failed', 'cancelled']);

function cancelDisabledReason(row: RobotTaskRow): string | null {
  if (row.paperclipRunStatus !== null && TERMINAL_PAPERCLIP_RUN_STATUSES.has(row.paperclipRunStatus)) {
    return `Cannot cancel — this task's Factory™ run already reached a terminal state ('${row.paperclipRunStatus}')`;
  }
  return null;
}

/**
 * Phase 119 (Plan 119-03 / D-119-08) — the question a task is waiting on: who
 * is being asked, for how long, and whether it climbed to someone else. The
 * Robot's WIP slot stays held while it waits (D-118-21), so an operator needs
 * to see this without opening the task. `title` is agent-written text: it is
 * rendered as a React text child (escaped), never as markup.
 */
function PendingQuestionLine({ question }: { question: PendingQuestionSummary }) {
  const waiting = formatDistanceToNowStrict(new Date(question.askedAt));
  return (
    <div className="mt-1 flex max-w-xs flex-col gap-0.5 whitespace-normal text-xs text-amber-700">
      <span>
        Question for {question.recipientName ?? 'nobody yet'} · waiting {waiting}
      </span>
      {question.title && (
        <span className="truncate text-gray-500" title={question.title}>
          &ldquo;{question.title}&rdquo;
        </span>
      )}
      {question.escalatedAt && (
        <span className="font-medium text-red-700">
          {question.escalationName
            ? `escalated to ${question.escalationName}`
            : 'escalated, nobody eligible'}
        </span>
      )}
    </div>
  );
}

export default function RobotTasksPage() {
  const navigate = useNavigate();
  const [state, setState] = useState('');
  const [projectId, setProjectId] = useState('');

  const filters = {
    // Factory™ is the only executor; Monka-routed rows are never listed.
    executor: 'paperclip' as const,
    state: state.trim() || undefined,
    projectId: projectId.trim() ? Number(projectId.trim()) : undefined,
  };

  const { data: rows, isLoading, error, refetch } = useRobotTasks(filters);
  const cancelMutation = useCancelRobotTask();
  const retryMutation = useRetryRobotTask();

  const [pendingRowAction, setPendingRowAction] = useState<RowAction | null>(null);

  const activeMutation = pendingRowAction?.kind === 'cancel' ? cancelMutation : retryMutation;

  const closeRowModal = () => {
    if (activeMutation.isPending) return;
    setPendingRowAction(null);
    cancelMutation.reset();
    retryMutation.reset();
  };

  const confirmRowAction = () => {
    if (!pendingRowAction) return;
    const mutation = pendingRowAction.kind === 'cancel' ? cancelMutation : retryMutation;
    mutation.mutate(pendingRowAction.row.taskId, {
      onSuccess: () => setPendingRowAction(null),
    });
  };

  const list = rows ?? [];

  return (
    <div className="space-y-6">
      <div>
        <button
          onClick={() => navigate('/admin')}
          className="text-sm text-gray-500 hover:text-gray-700 mb-1"
        >
          &larr; Back to Admin
        </button>
        <h1 className="text-2xl font-semibold text-gray-900">Factory™ Tasks</h1>
        <p className="text-sm text-gray-500 mt-1">
          Every task the Robot has handed to Factory™, with audited cancel and retry.
        </p>
        <div data-testid="paperclip-run-state-hint" className="mt-3 text-sm text-gray-500">
          <p>What the State column means for a Factory™ run:</p>
          <ul className="mt-1 space-y-1 list-disc list-inside">
            <li>running — Factory™ is working on the task right now.</li>
            <li>
              waiting_human — the run is paused until a human answers, and the Robot keeps its WIP
              slot held meanwhile.
            </li>
            <li>finished — the run completed. Nothing more happens on it.</li>
            <li>failed — the run ended with an error. Nothing more happens unless you retry.</li>
            <li>
              cancelled — the run was stopped, from this page or by Factory™. Nothing more
              happens unless you retry.
            </li>
          </ul>
        </div>
      </div>

      <div className="flex flex-wrap gap-3">
        <label className="flex flex-col text-sm text-gray-600">
          State
          <input
            type="text"
            value={state}
            onChange={(e) => setState(e.target.value)}
            placeholder="e.g. running, finished, in_flight"
            className="mt-1 rounded-md border border-gray-300 px-2 py-1 text-sm"
          />
        </label>
        <label className="flex flex-col text-sm text-gray-600">
          Project ID
          <input
            type="text"
            inputMode="numeric"
            value={projectId}
            onChange={(e) => setProjectId(e.target.value)}
            className="mt-1 rounded-md border border-gray-300 px-2 py-1 text-sm w-28"
          />
        </label>
      </div>

      {isLoading ? (
        <LoadingPage />
      ) : error ? (
        <div className="py-12 text-center">
          <p className="text-sm font-semibold text-red-600">Failed to load Factory™ tasks.</p>
          <p className="text-sm text-gray-500 mt-1">
            {error instanceof ApiClientError ? error.message : (error as Error).message}
          </p>
          <Button variant="secondary" size="sm" onClick={() => refetch()} className="mt-4">
            Try again
          </Button>
        </div>
      ) : list.length === 0 ? (
        <div className="py-12 text-center">
          <p className="text-sm text-gray-500">All quiet — no Factory™ tasks to show right now.</p>
        </div>
      ) : (
        <div className="overflow-auto rounded-lg border border-gray-200 bg-white">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">
                  Task
                </th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">
                  Project
                </th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">
                  State
                </th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">
                  Last observed
                </th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">
                  Attempt
                </th>
                <th scope="col" className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {list.map((row, idx) => {
                const disabledReason = cancelDisabledReason(row);
                return (
                  <tr key={row.taskId} className={idx % 2 === 1 ? 'bg-gray-50' : 'bg-white'}>
                    <td className="px-4 py-2 text-sm whitespace-nowrap">
                      <div className="flex flex-col">
                        <span className="font-medium text-gray-900">{row.taskName}</span>
                        <span className="text-xs text-gray-400">{row.taskCode ?? `#${row.taskId}`}</span>
                      </div>
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-700 whitespace-nowrap">
                      {row.projectName ?? `#${row.projectId}`}
                    </td>
                    <td className="px-4 py-2 text-sm whitespace-nowrap">
                      <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">
                        {row.executorState ?? '-'}
                      </span>
                      {row.stale && (
                        <span
                          title="No status update from Factory™ for over 2 hours — the run may have stopped without reporting."
                          className="ml-1 inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700"
                        >
                          stale
                        </span>
                      )}
                      {row.pendingQuestion && <PendingQuestionLine question={row.pendingQuestion} />}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-700 whitespace-nowrap">
                      {formatDisplayDateTime(row.executorStateAt)}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-700 whitespace-nowrap">
                      {row.paperclipDispatchAttempt}
                    </td>
                    <td className="px-4 py-2 text-right space-x-2">
                      <Button
                        variant="danger"
                        size="sm"
                        disabled={disabledReason !== null}
                        title={disabledReason ?? undefined}
                        onClick={() => setPendingRowAction({ kind: 'cancel', row })}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => setPendingRowAction({ kind: 'retry', row })}
                      >
                        Retry
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {pendingRowAction && (
        <ConfirmModal
          isOpen={true}
          onClose={closeRowModal}
          onConfirm={confirmRowAction}
          title={
            pendingRowAction.kind === 'cancel'
              ? `Cancel ${pendingRowAction.row.taskName}?`
              : `Retry ${pendingRowAction.row.taskName}?`
          }
          message={
            pendingRowAction.kind === 'cancel'
              ? 'This cancels the in-flight Factory™ run for this task. The action is audited with your name.'
              : 'This cancels any previous Factory™ issue for this task and dispatches a fresh one under a new attempt. The action is audited with your name.'
          }
          confirmText={pendingRowAction.kind === 'cancel' ? 'Cancel run' : 'Retry'}
          cancelText="Close"
          variant={pendingRowAction.kind === 'cancel' ? 'danger' : 'primary'}
          isLoading={activeMutation.isPending}
          error={
            activeMutation.isError
              ? activeMutation.error instanceof ApiClientError
                ? activeMutation.error.message
                : (activeMutation.error as Error).message
              : null
          }
        />
      )}
    </div>
  );
}
